import { useState, useEffect, useRef, useCallback } from 'react';
import axios from '@/utils/api';
import { 
  Image, 
  Plus, 
  Trash2, 
  Edit3, 
  ExternalLink, 
  Check,
  X,
  Upload,
  Eye,
  EyeOff,
  Calendar,
  Loader2,
  GripVertical,
  ImageOff,
  BarChart3,
  Users,
  MousePointerClick,
  TrendingUp,
  Search,
  Filter,
  ArrowUpDown,
  CheckCircle,
  Target,
  Download,
  FileText,
  Clock,
  Play,
  Pause,
  History
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

// Use same-origin API path on production custom domains
const API_BASE = window.location.hostname.includes('preview.emergentagent.com') || window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

// Normalize link URLs - add https:// if no protocol specified
const normalizeUrl = (url) => {
  if (!url) return '#';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return url;
  return `https://${url}`;
};

// Safe Banner Thumbnail component with fallback
const BannerThumbnail = ({ src, alt }) => {
  const [hasError, setHasError] = useState(false);
  
  if (hasError) {
    return (
      <div className="w-full h-full flex items-center justify-center text-[#FDF2F8]/30">
        <ImageOff className="w-6 h-6" />
      </div>
    );
  }
  
  return (
    <img 
      src={src} 
      alt={alt}
      className="w-full h-full object-cover"
      onError={() => setHasError(true)}
    />
  );
};

// Get admin auth headers
const getAdminAuthHeaders = () => {
  // With httpOnly cookies, auth is handled automatically via withCredentials: true
  // Return empty headers - the cookie will be sent automatically
  return {};
};

const TRANSITION_OPTIONS = [
  { value: '', label: 'Use Global Default' },
  { value: 'slide', label: 'Slide' },
  { value: 'fade', label: 'Fade' },
  { value: 'zoom', label: 'Zoom' },
  { value: 'flip', label: 'Flip' },
  { value: 'none', label: 'None (Instant)' }
];

const BannerManager = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const fileInputRef = useRef(null);
  
  // Analytics state
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [selectedBannerAnalytics, setSelectedBannerAnalytics] = useState(null);
  
  // Analytics filtering/sorting state
  const [clickerSearch, setClickerSearch] = useState('');
  const [clickerDateFilter, setClickerDateFilter] = useState('all'); // 'all', 'today', 'week', 'month'
  const [clickerSortBy, setClickerSortBy] = useState('newest'); // 'newest', 'oldest', 'clicks'
  const [togglingGoal, setTogglingGoal] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  
  // Global settings state
  const [globalSettings, setGlobalSettings] = useState({
    global_transition: 'slide',
    auto_rotate_interval: 5000
  });
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    image_url: '',
    mobile_image_url: '',
    link_url: '',
    alt_text: '',
    is_active: true,
    display_order: 0,
    start_date: '',
    end_date: '',
    transition: '',
    goal_label: '',
    campaign_notes: ''
  });

  useEffect(() => {
    fetchBanners();
    fetchGlobalSettings();
  }, []);
  
  const fetchGlobalSettings = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/banners/settings`, { 
        withCredentials: true 
      });
      setGlobalSettings(res.data);
    } catch (err) {
      console.error('Error fetching banner settings:', err);
    }
  };
  
  const saveGlobalSettings = async (newSettings) => {
    setSavingSettings(true);
    try {
      await axios.put(`${API_BASE}/api/admin/banners/settings`, newSettings, { 
        withCredentials: true 
      });
      setGlobalSettings(newSettings);
      toast.success('Settings saved');
    } catch (err) {
      console.error('Error saving settings:', err);
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // Fetch banner analytics
  const fetchAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const res = await axios.get(`${API_BASE}/api/admin/banners/analytics`, {
        withCredentials: true
      });
      setAnalyticsData(res.data.analytics || []);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      toast.error('Failed to load analytics');
    } finally {
      setLoadingAnalytics(false);
    }
  };

  // Fetch single banner detailed analytics
  const fetchBannerDetail = async (bannerId) => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/banners/${bannerId}/analytics`, {
        withCredentials: true
      });
      setSelectedBannerAnalytics(res.data);
    } catch (err) {
      console.error('Error fetching banner detail:', err);
      toast.error('Failed to load banner details');
    }
  };

  // Toggle goal completion for a clicker
  const toggleGoalCompletion = async (bannerId, userId, currentStatus) => {
    setTogglingGoal(userId);
    try {
      await axios.post(`${API_BASE}/api/admin/banners/${bannerId}/goal/${userId}`, {
        goal_completed: !currentStatus
      }, { withCredentials: true });
      
      // Refresh the banner analytics
      await fetchBannerDetail(bannerId);
      toast.success(!currentStatus ? 'Goal marked as completed!' : 'Goal unmarked');
    } catch (err) {
      console.error('Error toggling goal:', err);
      toast.error('Failed to update goal status');
    } finally {
      setTogglingGoal(null);
    }
  };

  // Filter and sort clickers
  const getFilteredClickers = (clickers) => {
    if (!clickers) return [];
    
    let filtered = [...clickers];
    
    // Search filter
    if (clickerSearch) {
      const search = clickerSearch.toLowerCase();
      filtered = filtered.filter(c => 
        c.name?.toLowerCase().includes(search) ||
        c.email?.toLowerCase().includes(search)
      );
    }
    
    // Date filter
    const now = new Date();
    if (clickerDateFilter === 'today') {
      const today = now.toISOString().split('T')[0];
      filtered = filtered.filter(c => c.last_click?.startsWith(today));
    } else if (clickerDateFilter === 'week') {
      const weekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString();
      filtered = filtered.filter(c => c.last_click >= weekAgo);
    } else if (clickerDateFilter === 'month') {
      const monthAgo = new Date(now - 30 * 24 * 60 * 60 * 1000).toISOString();
      filtered = filtered.filter(c => c.last_click >= monthAgo);
    }
    
    // Sort
    if (clickerSortBy === 'newest') {
      filtered.sort((a, b) => (b.last_click || '').localeCompare(a.last_click || ''));
    } else if (clickerSortBy === 'oldest') {
      filtered.sort((a, b) => (a.last_click || '').localeCompare(b.last_click || ''));
    } else if (clickerSortBy === 'clicks') {
      filtered.sort((a, b) => b.click_count - a.click_count);
    }
    
    return filtered;
  };

  // Download PDF report
  const downloadPdfReport = async (bannerId) => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API_BASE}/api/admin/banners/${bannerId}/export-pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `campaign_report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded');
    } catch (err) {
      console.error('Error downloading PDF:', err);
      toast.error('Failed to download report');
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Open analytics modal
  const handleOpenAnalytics = () => {
    setShowAnalytics(true);
    fetchAnalytics();
  };

  const fetchBanners = async () => {
    try {
      console.log('Fetching banners with httpOnly cookie auth');
      const res = await axios.get(`${API_BASE}/api/admin/banners`, { 
        withCredentials: true
      });
      console.log('Banners received:', res.data.banners?.length || 0);
      setBanners(res.data.banners || []);
    } catch (err) {
      console.error('Error fetching banners:', err);
      if (err.response?.status === 401) {
        toast.error('Session expired. Please log out and log back in.');
      } else {
        toast.error('Failed to load banners. Try refreshing the page.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, GIF, or WebP image');
      return;
    }

    // Validate file size (max 25MB for large 2500x2500 300dpi images)
    const maxSize = 25 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`Image must be less than 25MB. Your file is ${(file.size / (1024 * 1024)).toFixed(1)}MB`);
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const res = await axios.post(`${API_BASE}/api/admin/banners/upload`, formDataUpload, {
        withCredentials: true,
        headers: { 
          'Content-Type': 'multipart/form-data',
          ...getAdminAuthHeaders()
        }
      });
      
      if (res.data.url) {
        setFormData({ ...formData, image_url: res.data.url });
        toast.success('Image uploaded successfully');
      } else {
        toast.error('Upload failed - no URL returned');
      }
    } catch (err) {
      console.error('Upload error:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to upload image';
      toast.error(`Upload failed: ${errorMsg}`);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.image_url || !formData.link_url) {
      toast.error('Image and Link URL are required');
      return;
    }

    try {
      if (editingId) {
        await axios.put(`${API_BASE}/api/admin/banners/${editingId}`, formData, { 
          withCredentials: true,
          headers: getAdminAuthHeaders()
        });
        toast.success('Banner updated');
      } else {
        await axios.post(`${API_BASE}/api/admin/banners`, {
          ...formData,
          display_order: banners.length
        }, { 
          withCredentials: true,
          headers: getAdminAuthHeaders()
        });
        toast.success('Banner created');
      }
      
      resetForm();
      fetchBanners();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save banner');
    }
  };

  const handleDelete = async (bannerId) => {
    if (!confirm('Are you sure you want to delete this banner?')) return;
    
    try {
      await axios.delete(`${API_BASE}/api/admin/banners/${bannerId}`, { 
        withCredentials: true,
        headers: getAdminAuthHeaders()
      });
      toast.success('Banner deleted');
      fetchBanners();
    } catch (err) {
      toast.error('Failed to delete banner');
    }
  };

  const handleToggleActive = async (banner) => {
    try {
      await axios.put(`${API_BASE}/api/admin/banners/${banner.id}`, {
        is_active: !banner.is_active
      }, { 
        withCredentials: true,
        headers: getAdminAuthHeaders()
      });
      toast.success(banner.is_active ? 'Banner deactivated' : 'Banner activated');
      fetchBanners();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update banner');
    }
  };

  const handleEdit = (banner) => {
    setFormData({
      image_url: banner.image_url,
      mobile_image_url: banner.mobile_image_url || '',
      link_url: banner.link_url,
      alt_text: banner.alt_text || '',
      is_active: banner.is_active,
      display_order: banner.display_order,
      start_date: banner.start_date || '',
      end_date: banner.end_date || '',
      transition: banner.transition || '',
      goal_label: banner.goal_label || '',
      campaign_notes: banner.campaign_notes || ''
    });
    setEditingId(banner.id);
    setShowAddForm(true);
  };

  const resetForm = () => {
    setFormData({
      image_url: '',
      mobile_image_url: '',
      link_url: '',
      alt_text: '',
      is_active: true,
      display_order: 0,
      start_date: '',
      end_date: '',
      transition: '',
      goal_label: '',
      campaign_notes: ''
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Add a slight delay to allow the drag image to be created
    setTimeout(() => {
      e.target.style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (index !== draggedIndex) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    setDragOverIndex(null);
    
    if (draggedIndex === null || draggedIndex === dropIndex) return;
    
    // Reorder the banners array locally
    const newBanners = [...banners];
    const [draggedBanner] = newBanners.splice(draggedIndex, 1);
    newBanners.splice(dropIndex, 0, draggedBanner);
    
    // Update local state immediately for smooth UX
    setBanners(newBanners);
    
    // Send reorder request to backend
    try {
      const bannerIds = newBanners.map(b => b.id);
      await axios.post(`${API_BASE}/api/admin/banners/reorder`, 
        { banner_ids: bannerIds },
        { 
          withCredentials: true,
          headers: getAdminAuthHeaders()
        }
      );
      toast.success('Banner order updated');
    } catch (err) {
      console.error('Reorder error:', err);
      toast.error('Failed to update order');
      // Revert on error
      fetchBanners();
    }
  };

  const activeCount = banners.filter(b => b.is_active).length;

  return (
    <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <Image className="w-5 h-5 text-[#df1283]" />
          Banner Management
          <span className="text-xs text-[#FDF2F8]/50 font-normal ml-2">
            ({activeCount} active)
          </span>
        </h2>
        <div className="flex items-center gap-2">
          <Button 
            onClick={handleOpenAnalytics}
            size="sm"
            variant="outline"
            className="border-[#4A0A66] text-[#0FFFE2] hover:bg-[#0FFFE2]/10"
          >
            <BarChart3 className="w-4 h-4 mr-1" />
            Analytics
          </Button>
          <Button 
            onClick={() => setShowAddForm(!showAddForm)}
            size="sm"
            className="bg-gradient-to-r from-[#df1283] to-[#FF7901]"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Banner
          </Button>
        </div>
      </div>
      
      {/* Global Settings */}
      <div className="mb-4 p-3 bg-[#4A0A66]/20 rounded-lg border border-[#4A0A66]/30">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <div>
              <label className="text-xs text-[#FDF2F8]/60 block mb-1">Global Transition Effect</label>
              <select
                value={globalSettings.global_transition}
                onChange={(e) => saveGlobalSettings({ ...globalSettings, global_transition: e.target.value })}
                disabled={savingSettings}
                className="bg-[#2D063E] border border-[#4A0A66] text-white text-sm px-3 py-1.5 rounded-lg focus:outline-none focus:border-[#df1283]"
              >
                <option value="slide">Slide</option>
                <option value="fade">Fade</option>
                <option value="zoom">Zoom</option>
                <option value="flip">Flip</option>
                <option value="none">None (Instant)</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-[#FDF2F8]/60 block mb-1">Auto-Rotate Interval</label>
              <select
                value={globalSettings.auto_rotate_interval}
                onChange={(e) => saveGlobalSettings({ ...globalSettings, auto_rotate_interval: parseInt(e.target.value) })}
                disabled={savingSettings}
                className="bg-[#2D063E] border border-[#4A0A66] text-white text-sm px-3 py-1.5 rounded-lg focus:outline-none focus:border-[#df1283]"
              >
                <option value={3000}>3 seconds</option>
                <option value={5000}>5 seconds</option>
                <option value={7000}>7 seconds</option>
                <option value={10000}>10 seconds</option>
                <option value={15000}>15 seconds</option>
              </select>
            </div>
          </div>
          {savingSettings && <span className="text-xs text-[#0FFFE2]">Saving...</span>}
        </div>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <form onSubmit={handleSubmit} className="mb-4 p-4 bg-[#4A0A66]/20 rounded-lg border border-[#4A0A66]/30">
          <h3 className="text-white font-semibold mb-3">
            {editingId ? 'Edit Banner' : 'Add New Banner'}
          </h3>
          
          <div className="grid gap-3">
            {/* Image Upload Section */}
            <div>
              <label className="text-xs text-[#FDF2F8]/60 mb-1 block">Banner Image *</label>
              
              {/* Upload Button */}
              <div className="flex gap-2 mb-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="banner-upload"
                />
                <Button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex-1 bg-[#4A0A66] hover:bg-[#4A0A66]/80 border border-[#df1283]/30"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Image
                    </>
                  )}
                </Button>
              </div>
              
              {/* Or enter URL manually */}
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-1 h-px bg-[#4A0A66]/50" />
                <span className="text-xs text-[#FDF2F8]/40">or paste URL</span>
                <div className="flex-1 h-px bg-[#4A0A66]/50" />
              </div>
              
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://example.com/banner.jpg"
                className="bg-[#2D063E] border-[#4A0A66] text-white text-sm"
              />
              <p className="text-xs text-[#FDF2F8]/40 mt-1">Recommended: 1200x400px (Desktop)</p>
            </div>
            
            {/* Mobile Image (Optional) */}
            <div>
              <label className="text-xs text-[#FDF2F8]/60 mb-1 block">Mobile Image (Optional)</label>
              <Input
                value={formData.mobile_image_url}
                onChange={(e) => setFormData({ ...formData, mobile_image_url: e.target.value })}
                placeholder="https://example.com/banner-mobile.jpg"
                className="bg-[#2D063E] border-[#4A0A66] text-white text-sm"
              />
              <p className="text-xs text-[#FDF2F8]/40 mt-1">Recommended: 600x300px (shown on phones). If empty, desktop image is used.</p>
            </div>
            
            <div>
              <label className="text-xs text-[#FDF2F8]/60 mb-1 block">Link URL * (where banner links to)</label>
              <Input
                value={formData.link_url}
                onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                placeholder="https://example.com/promo"
                className="bg-[#2D063E] border-[#4A0A66] text-white"
              />
            </div>
            
            <div>
              <label className="text-xs text-[#FDF2F8]/60 mb-1 block">Alt Text (for accessibility)</label>
              <Input
                value={formData.alt_text}
                onChange={(e) => setFormData({ ...formData, alt_text: e.target.value })}
                placeholder="Promotional banner description"
                className="bg-[#2D063E] border-[#4A0A66] text-white"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#FDF2F8]/60 mb-1 block">Start Date (optional)</label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="bg-[#2D063E] border-[#4A0A66] text-white"
                />
              </div>
              <div>
                <label className="text-xs text-[#FDF2F8]/60 mb-1 block">End Date (optional)</label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="bg-[#2D063E] border-[#4A0A66] text-white"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="is_active" className="text-sm text-[#FDF2F8]/80">Active (visible on dashboard)</label>
            </div>
            
            {/* Per-Banner Transition Override */}
            <div>
              <label className="text-xs text-[#FDF2F8]/60 mb-1 block">Transition Effect</label>
              <select
                value={formData.transition || ''}
                onChange={(e) => setFormData({ ...formData, transition: e.target.value || null })}
                className="w-full bg-[#2D063E] border border-[#4A0A66] text-white text-sm px-3 py-2 rounded-lg focus:outline-none focus:border-[#df1283]"
              >
                {TRANSITION_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <p className="text-xs text-[#FDF2F8]/40 mt-1">Leave as &ldquo;Use Global Default&rdquo; to use the global setting</p>
            </div>
            
            {/* Campaign Goal Label */}
            <div>
              <label className="text-xs text-[#FDF2F8]/60 mb-1 block flex items-center gap-2">
                <Target className="w-3 h-3 text-green-400" />
                Goal Label (what counts as a conversion)
              </label>
              <Input
                value={formData.goal_label || ''}
                onChange={(e) => setFormData({ ...formData, goal_label: e.target.value })}
                placeholder="e.g., Joined Community, Registered, Purchased"
                className="bg-[#2D063E] border-[#4A0A66] text-white"
              />
              <p className="text-xs text-[#FDF2F8]/40 mt-1">Name the desired action for tracking conversions</p>
            </div>
            
            {/* Campaign Notes */}
            <div>
              <label className="text-xs text-[#FDF2F8]/60 mb-1 block flex items-center gap-2">
                <FileText className="w-3 h-3 text-[#FF7901]" />
                Campaign Notes
              </label>
              <textarea
                value={formData.campaign_notes || ''}
                onChange={(e) => setFormData({ ...formData, campaign_notes: e.target.value })}
                placeholder="e.g., Labor Day Promo, Partnership with XYZ"
                rows={2}
                className="w-full bg-[#2D063E] border border-[#4A0A66] text-white text-sm px-3 py-2 rounded-lg focus:outline-none focus:border-[#df1283]"
              />
              <p className="text-xs text-[#FDF2F8]/40 mt-1">Add notes to remember campaign context</p>
            </div>

            {/* Preview */}
            {formData.image_url && (
              <div className="mt-2">
                <label className="text-xs text-[#FDF2F8]/60 mb-1 block">Preview</label>
                <img 
                  src={formData.image_url} 
                  alt="Banner preview" 
                  className="max-w-full h-auto rounded-lg border border-[#4A0A66]/30"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            )}
          </div>
          
          <div className="flex gap-2 mt-4">
            <Button type="submit" size="sm" className="bg-gradient-to-r from-[#df1283] to-[#FF7901]">
              <Check className="w-4 h-4 mr-1" />
              {editingId ? 'Update' : 'Create'}
            </Button>
            <Button type="button" onClick={resetForm} variant="ghost" size="sm" className="text-[#FDF2F8]/60">
              <X className="w-4 h-4 mr-1" />
              Cancel
            </Button>
          </div>
        </form>
      )}

      {/* Banners List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="w-8 h-8 border-2 border-[#df1283] border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : banners.length === 0 ? (
        <div className="text-center py-8 text-[#FDF2F8]/50">
          <Image className="w-12 h-12 mx-auto mb-2 opacity-30" />
          <p>No banners yet. Add your first banner!</p>
        </div>
      ) : (
        <div className="space-y-2">
          {banners.length > 1 && (
            <p className="text-xs text-[#FDF2F8]/40 mb-2 flex items-center gap-1">
              <GripVertical className="w-3 h-3" /> Drag to reorder banners
            </p>
          )}
          {banners.map((banner, index) => (
            <div 
              key={banner.id}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              className={`flex items-center gap-3 p-3 rounded-lg border transition-all cursor-move ${
                banner.is_active 
                  ? 'bg-[#4A0A66]/30 border-[#df1283]/30' 
                  : 'bg-[#1a0527]/50 border-[#4A0A66]/20 opacity-60'
              } ${dragOverIndex === index ? 'border-[#FF7901] border-2 scale-[1.02]' : ''}`}
            >
              {/* Drag handle */}
              <div className="text-[#FDF2F8]/30 hover:text-[#FDF2F8]/60 cursor-grab active:cursor-grabbing">
                <GripVertical className="w-5 h-5" />
              </div>
              
              {/* Order indicator */}
              <div className="text-[#FDF2F8]/30 text-sm font-mono w-6 text-center">
                #{index + 1}
              </div>
              
              {/* Thumbnail */}
              <div className="w-20 h-12 rounded overflow-hidden bg-[#2D063E] flex-shrink-0">
                <BannerThumbnail src={banner.image_url} alt={banner.alt_text} />
              </div>
              
              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    banner.is_active 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-[#4A0A66]/50 text-[#FDF2F8]/50'
                  }`}>
                    {banner.is_active ? 'Active' : 'Inactive'}
                  </span>
                  {banner.start_date && banner.start_date !== '' && (
                    <span className="text-xs text-[#FDF2F8]/40 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {banner.start_date.split('T')[0]}
                      {banner.end_date && banner.end_date !== '' && ` → ${banner.end_date.split('T')[0]}`}
                    </span>
                  )}
                </div>
                <a 
                  href={normalizeUrl(banner.link_url)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-[#df1283] hover:underline truncate block"
                >
                  {banner.link_url}
                  <ExternalLink className="w-3 h-3 inline ml-1" />
                </a>
              </div>
              
              {/* Actions */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleToggleActive(banner)}
                  className="p-2 hover:bg-[#4A0A66]/50 rounded-lg transition-colors"
                  title={banner.is_active ? 'Deactivate' : 'Activate'}
                >
                  {banner.is_active ? (
                    <EyeOff className="w-4 h-4 text-[#FDF2F8]/50" />
                  ) : (
                    <Eye className="w-4 h-4 text-green-400" />
                  )}
                </button>
                <button
                  onClick={() => handleEdit(banner)}
                  className="p-2 hover:bg-[#4A0A66]/50 rounded-lg transition-colors"
                  title="Edit"
                >
                  <Edit3 className="w-4 h-4 text-[#FDF2F8]/50" />
                </button>
                <button
                  onClick={() => handleDelete(banner.id)}
                  className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      <p className="text-xs text-[#FDF2F8]/30 mt-4 text-center">
        Banners rotate automatically on the dashboard.
      </p>

      {/* Analytics Modal */}
      {showAnalytics && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-[#4A0A66]">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[#0FFFE2]" />
                Banner Analytics
              </h3>
              <button
                onClick={() => {
                  setShowAnalytics(false);
                  setSelectedBannerAnalytics(null);
                }}
                className="p-2 rounded-lg hover:bg-[#4A0A66]/50 text-[#FDF2F8]/70 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 overflow-y-auto max-h-[calc(85vh-80px)]">
              {loadingAnalytics ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#df1283]" />
                </div>
              ) : selectedBannerAnalytics ? (
                /* Detailed view for a single banner */
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => setSelectedBannerAnalytics(null)}
                      className="text-sm text-[#0FFFE2] hover:underline flex items-center gap-1"
                    >
                      ← Back to all banners
                    </button>
                    <Button
                      onClick={() => downloadPdfReport(selectedBannerAnalytics.banner.id)}
                      disabled={downloadingPdf}
                      variant="outline"
                      size="sm"
                      className="border-[#df1283] text-[#df1283] hover:bg-[#df1283]/10"
                    >
                      {downloadingPdf ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Export PDF
                    </Button>
                  </div>
                  
                  <div className="flex items-center gap-4 p-4 bg-[#4A0A66]/20 rounded-xl">
                    {selectedBannerAnalytics.banner.image_url && (
                      <img 
                        src={selectedBannerAnalytics.banner.image_url} 
                        alt={selectedBannerAnalytics.banner.title}
                        className="w-24 h-16 object-cover rounded-lg"
                      />
                    )}
                    <div className="flex-1">
                      <h4 className="font-bold text-white">{selectedBannerAnalytics.banner.title}</h4>
                      <p className="text-xs text-[#FDF2F8]/50 truncate max-w-xs">
                        {selectedBannerAnalytics.banner.link_url}
                      </p>
                      {selectedBannerAnalytics.banner.goal_label && (
                        <p className="text-xs text-[#0FFFE2] mt-1 flex items-center gap-1">
                          <Target className="w-3 h-3" />
                          Goal: {selectedBannerAnalytics.banner.goal_label}
                        </p>
                      )}
                    </div>
                    {/* Conversion Rate Badge */}
                    <div className="text-right">
                      <div className="text-2xl font-bold text-green-400">
                        {selectedBannerAnalytics.stats.unique_clickers > 0 
                          ? Math.round((selectedBannerAnalytics.stats.goals_completed || 0) / selectedBannerAnalytics.stats.unique_clickers * 100)
                          : 0}%
                      </div>
                      <div className="text-xs text-green-400/70">Conversion Rate</div>
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-5 gap-3">
                    <div className="bg-[#4A0A66]/30 p-3 rounded-lg text-center">
                      <Eye className="w-5 h-5 mx-auto mb-1 text-[#FDF2F8]/50" />
                      <div className="text-2xl font-bold text-white">{selectedBannerAnalytics.stats.impressions}</div>
                      <div className="text-xs text-[#FDF2F8]/50">Views</div>
                    </div>
                    <div className="bg-[#4A0A66]/30 p-3 rounded-lg text-center">
                      <MousePointerClick className="w-5 h-5 mx-auto mb-1 text-[#df1283]" />
                      <div className="text-2xl font-bold text-white">{selectedBannerAnalytics.stats.clicks}</div>
                      <div className="text-xs text-[#FDF2F8]/50">Clicks</div>
                    </div>
                    <div className="bg-[#4A0A66]/30 p-3 rounded-lg text-center">
                      <TrendingUp className="w-5 h-5 mx-auto mb-1 text-[#0FFFE2]" />
                      <div className="text-2xl font-bold text-white">{selectedBannerAnalytics.stats.ctr}%</div>
                      <div className="text-xs text-[#FDF2F8]/50">CTR</div>
                    </div>
                    <div className="bg-[#4A0A66]/30 p-3 rounded-lg text-center">
                      <Users className="w-5 h-5 mx-auto mb-1 text-[#FF7901]" />
                      <div className="text-2xl font-bold text-white">{selectedBannerAnalytics.stats.unique_clickers}</div>
                      <div className="text-xs text-[#FDF2F8]/50">Unique</div>
                    </div>
                    <div className="bg-green-500/20 p-3 rounded-lg text-center border border-green-500/30">
                      <Target className="w-5 h-5 mx-auto mb-1 text-green-400" />
                      <div className="text-2xl font-bold text-green-400">{selectedBannerAnalytics.stats.goals_completed || 0}</div>
                      <div className="text-xs text-green-400/70">Goals Met</div>
                    </div>
                  </div>

                  {/* Who Clicked - Enhanced */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="font-semibold text-white flex items-center gap-2">
                        <Users className="w-4 h-4 text-[#df1283]" />
                        Who Clicked ({selectedBannerAnalytics.clickers.length})
                      </h5>
                    </div>
                    
                    {/* Filters & Search */}
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <div className="relative flex-1 min-w-[200px]">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#FDF2F8]/40" />
                        <Input
                          placeholder="Search by name or email..."
                          value={clickerSearch}
                          onChange={(e) => setClickerSearch(e.target.value)}
                          className="pl-9 h-8 bg-[#1a0527] border-[#4A0A66] text-white text-sm"
                        />
                      </div>
                      
                      <select
                        value={clickerDateFilter}
                        onChange={(e) => setClickerDateFilter(e.target.value)}
                        className="h-8 px-2 rounded-md bg-[#1a0527] border border-[#4A0A66] text-white text-sm"
                      >
                        <option value="all">All Time</option>
                        <option value="today">Today</option>
                        <option value="week">Last 7 Days</option>
                        <option value="month">Last 30 Days</option>
                      </select>
                      
                      <select
                        value={clickerSortBy}
                        onChange={(e) => setClickerSortBy(e.target.value)}
                        className="h-8 px-2 rounded-md bg-[#1a0527] border border-[#4A0A66] text-white text-sm"
                      >
                        <option value="newest">Newest First</option>
                        <option value="oldest">Oldest First</option>
                        <option value="clicks">Most Clicks</option>
                      </select>
                    </div>
                    
                    {selectedBannerAnalytics.clickers.length === 0 ? (
                      <p className="text-sm text-[#FDF2F8]/50 py-4 text-center bg-[#4A0A66]/20 rounded-lg">
                        No clicks recorded yet
                      </p>
                    ) : (
                      <div className="bg-[#1a0527] rounded-lg border border-[#4A0A66]/50 overflow-hidden max-h-80 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-[#4A0A66]/30 sticky top-0">
                            <tr>
                              <th className="text-left p-2 text-[#FDF2F8]/70">User</th>
                              <th className="text-left p-2 text-[#FDF2F8]/70">Email</th>
                              <th className="text-center p-2 text-[#FDF2F8]/70">Clicks</th>
                              <th className="text-left p-2 text-[#FDF2F8]/70">Last Click</th>
                              <th className="text-center p-2 text-[#FDF2F8]/70">Goal Met</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getFilteredClickers(selectedBannerAnalytics.clickers).map((clicker, idx) => (
                              <tr key={idx} className={`border-t border-[#4A0A66]/30 hover:bg-[#4A0A66]/20 ${clicker.goal_completed ? 'bg-green-500/5' : ''}`}>
                                <td className="p-2 text-white">
                                  <div className="flex items-center gap-2">
                                    {clicker.goal_completed && (
                                      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                                    )}
                                    {clicker.name || 'Unknown'}
                                  </div>
                                </td>
                                <td className="p-2 text-[#FDF2F8]/70 text-xs">{clicker.email}</td>
                                <td className="p-2 text-center">
                                  <span className="px-2 py-0.5 bg-[#df1283]/20 text-[#df1283] rounded-full text-xs">
                                    {clicker.click_count}
                                  </span>
                                </td>
                                <td className="p-2 text-[#FDF2F8]/50 text-xs">
                                  {new Date(clicker.last_click).toLocaleDateString('en-US', { 
                                    month: 'short', 
                                    day: 'numeric',
                                    year: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit'
                                  })}
                                </td>
                                <td className="p-2 text-center">
                                  <button
                                    onClick={() => toggleGoalCompletion(
                                      selectedBannerAnalytics.banner.id,
                                      clicker.user_id,
                                      clicker.goal_completed
                                    )}
                                    disabled={togglingGoal === clicker.user_id}
                                    className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${
                                      clicker.goal_completed
                                        ? 'bg-green-500 border-green-500 text-white'
                                        : 'border-[#4A0A66] hover:border-green-500/50 text-transparent hover:text-green-500/50'
                                    }`}
                                    title={clicker.goal_completed ? 'Mark as incomplete' : 'Mark goal as met'}
                                  >
                                    {togglingGoal === clicker.user_id ? (
                                      <Loader2 className="w-3 h-3 animate-spin text-white" />
                                    ) : (
                                      <Check className="w-3 h-3" />
                                    )}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {getFilteredClickers(selectedBannerAnalytics.clickers).length === 0 && (
                          <p className="text-sm text-[#FDF2F8]/50 py-4 text-center">
                            No results match your filters
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                /* Summary view */
                <div className="space-y-4">
                  <p className="text-sm text-[#FDF2F8]/60 mb-4">
                    Click on a banner to view detailed analytics including who clicked.
                  </p>
                  
                  {analyticsData && analyticsData.length > 0 ? (
                    <div className="bg-[#1a0527] rounded-lg border border-[#4A0A66]/50 overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-[#4A0A66]/30">
                          <tr>
                            <th className="text-left p-3 text-[#FDF2F8]/70">Banner</th>
                            <th className="text-center p-3 text-[#FDF2F8]/70">Views</th>
                            <th className="text-center p-3 text-[#FDF2F8]/70">Clicks</th>
                            <th className="text-center p-3 text-[#FDF2F8]/70">CTR</th>
                            <th className="text-center p-3 text-[#FDF2F8]/70">Unique</th>
                            <th className="text-center p-3 text-[#FDF2F8]/70">Goals</th>
                            <th className="text-center p-3 text-[#FDF2F8]/70">Conv.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analyticsData.map((banner) => (
                            <tr 
                              key={banner.banner_id}
                              onClick={() => fetchBannerDetail(banner.banner_id)}
                              className="border-t border-[#4A0A66]/30 hover:bg-[#4A0A66]/20 cursor-pointer transition-colors"
                            >
                              <td className="p-3">
                                <div className="flex items-center gap-3">
                                  {banner.image_url && (
                                    <img 
                                      src={banner.image_url} 
                                      alt={banner.title}
                                      className="w-12 h-8 object-cover rounded"
                                    />
                                  )}
                                  <div>
                                    <div className="text-white font-medium">{banner.title}</div>
                                    <div className="text-xs text-[#FDF2F8]/40 flex items-center gap-2">
                                      {banner.is_active ? (
                                        <span className="text-green-400 flex items-center gap-1">
                                          <Play className="w-3 h-3" /> Active
                                        </span>
                                      ) : (
                                        <span className="text-[#FDF2F8]/40 flex items-center gap-1">
                                          <Pause className="w-3 h-3" /> Inactive
                                        </span>
                                      )}
                                      {banner.goal_label && (
                                        <span className="text-[#0FFFE2]">• {banner.goal_label}</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="p-3 text-center text-white">{banner.impressions?.toLocaleString()}</td>
                              <td className="p-3 text-center">
                                <span className="px-2 py-0.5 bg-[#df1283]/20 text-[#df1283] rounded-full">
                                  {banner.clicks}
                                </span>
                              </td>
                              <td className="p-3 text-center text-[#0FFFE2]">{banner.ctr}%</td>
                              <td className="p-3 text-center text-white">{banner.unique_clickers}</td>
                              <td className="p-3 text-center">
                                <span className={`px-2 py-0.5 rounded-full ${
                                  banner.goals_completed > 0 
                                    ? 'bg-green-500/20 text-green-400' 
                                    : 'bg-[#4A0A66]/30 text-[#FDF2F8]/50'
                                }`}>
                                  {banner.goals_completed || 0}
                                </span>
                              </td>
                              <td className="p-3 text-center">
                                <span className={`font-medium ${
                                  banner.conversion_rate > 0 ? 'text-green-400' : 'text-[#FDF2F8]/50'
                                }`}>
                                  {banner.conversion_rate || 0}%
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-[#FDF2F8]/50">
                      <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No analytics data yet.</p>
                      <p className="text-xs mt-1">Data will appear once users view and click your banners.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BannerManager;
